<script setup lang="ts">
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import Dialog from 'primevue/dialog'
import InputText from 'primevue/inputtext'
import {
    Server, Hash, Sun, Languages, Eye, Wifi, Download, AlertTriangle
} from 'lucide-vue-next'
import BBtn from '@lib/components/base/BBtn.vue'
import { useClipboardStore } from '@/stores/clipboard'
import type { ServiceVariant, DisplayPercent, LanguageCode, ScanInfoMode } from '@/lib/serviceCode'
import { useToast } from '@lib/composables/useToast'

defineProps<{ modelValue: boolean }>()
const emit = defineEmits<{ (e: 'update:modelValue', v: boolean): void }>()

function close() {
    emit('update:modelValue', false)
}

const { t } = useI18n()
const clipboard = useClipboardStore()
const toast = useToast()

const DISPLAY_PRESETS: DisplayPercent[] = [20, 40, 60, 80, 100]
const LANGS: LanguageCode[] = ['cs', 'en', 'sk', 'de', 'pl', 'hu', 'hr']
const SCAN_MODES: ScanInfoMode[] = ['NONE', 'PRICE', 'TICKETSET', 'TICKETSET-PRICE']

// WIFI is the only parametric service. Click expands an inline form, submit
// validates and adds; the form is reset and collapsed on success. No prefill,
// no history — fields are always freshly empty when expanded.
const wifiExpanded = ref(false)
const wifiSsid = ref('')
const wifiPassword = ref('')

// `add()` takes the full-label i18n key for the toast (e.g. clipboard.service.server.prod
// = 'SERVER prod'), while the tile itself shows only the short variant (e.g. 'prod').
function add(service: ServiceVariant, labelKey: string, labelParams?: Record<string, string | number>) {
    clipboard.addService(service)
    toast.success(t('clipboard.service.addedToast', { label: t(labelKey, labelParams ?? {}) }))
}

function addWifi() {
    if (!wifiSsid.value.trim() || !wifiPassword.value.trim()) return
    clipboard.addService({ kind: 'wifi', variant: { ssid: wifiSsid.value, password: wifiPassword.value } })
    toast.success(t('clipboard.service.addedToast', {
        label: t('clipboard.service.wifi.label', { ssid: wifiSsid.value })
    }))
    wifiSsid.value = ''
    wifiPassword.value = ''
    wifiExpanded.value = false
}
</script>

<template>
    <Dialog
        :visible="modelValue"
        modal
        :header="t('clipboard.service.modal.title')"
        :style="{ width: '720px', maxWidth: '95vw' }"
        @update:visible="(v: boolean) => emit('update:modelValue', v)"
    >
        <!--
            Two main groups: 'reader' (HW only) and 'scanner' (HW + mobile). Inside
            each group, every prefix has its own sub-heading (SERVER, DISPLAY, …) and
            below it a grid of tiles. Tiles render only the variant value (e.g. 'prod',
            '60 %') — the heading carries the prefix context. Click adds the code to the
            clipboard and toasts; modal stays open so multiple codes can be chained.
        -->
        <section class="mb-4">
            <h3 class="text-sm font-semibold mb-3 flex items-center gap-2">
                {{ t('clipboard.service.groups.reader') }}
                <span class="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                    {{ t('clipboard.service.groups.hwOnlyBadge') }}
                </span>
            </h3>

            <!-- SERVER -->
            <div class="mb-3">
                <div class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 flex items-center gap-1.5">
                    <Server class="w-3.5 h-3.5" />
                    {{ t('clipboard.service.server.heading') }}
                    <span class="font-normal normal-case text-gray-400">— {{ t('clipboard.service.server.subtitle') }}</span>
                </div>
                <div class="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    <button
                        type="button"
                        class="border rounded-12 p-2 bg-white hover:bg-gray-50 text-center text-sm"
                        data-test="tile-server-prod"
                        @click="add({ kind: 'server', variant: 'prod' }, 'clipboard.service.server.prod')"
                    >
                        {{ t('clipboard.service.server.short.prod') }}
                    </button>
                    <button
                        type="button"
                        class="border rounded-12 p-2 bg-white hover:bg-gray-50 text-center text-sm"
                        data-test="tile-server-test"
                        @click="add({ kind: 'server', variant: 'test' }, 'clipboard.service.server.test')"
                    >
                        {{ t('clipboard.service.server.short.test') }}
                    </button>
                </div>
            </div>

            <!-- DISPLAY -->
            <div class="mb-3">
                <div class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 flex items-center gap-1.5">
                    <Sun class="w-3.5 h-3.5" />
                    {{ t('clipboard.service.display.heading') }}
                    <span class="font-normal normal-case text-gray-400">— {{ t('clipboard.service.display.subtitle') }}</span>
                </div>
                <div class="grid grid-cols-3 sm:grid-cols-5 gap-2">
                    <button
                        v-for="pct in DISPLAY_PRESETS"
                        :key="pct"
                        type="button"
                        class="border rounded-12 p-2 bg-white hover:bg-gray-50 text-center text-sm"
                        :data-test="`tile-display-${pct}`"
                        @click="add({ kind: 'display', variant: pct }, 'clipboard.service.display.label', { pct })"
                    >
                        {{ t('clipboard.service.display.short', { pct }) }}
                    </button>
                </div>
            </div>

            <!-- WIFI (parametric — inline form below the tile) -->
            <div class="mb-3">
                <div class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 flex items-center gap-1.5">
                    <Wifi class="w-3.5 h-3.5" />
                    {{ t('clipboard.service.wifi.heading') }}
                    <span class="font-normal normal-case text-gray-400">— {{ t('clipboard.service.wifi.subtitle') }}</span>
                </div>
                <button
                    type="button"
                    class="border rounded-12 p-2 bg-white hover:bg-gray-50 w-full text-left text-sm flex items-center gap-2"
                    data-test="tile-wifi"
                    @click="wifiExpanded = !wifiExpanded"
                >
                    <span>{{ t('clipboard.service.wifi.expandHint') }}</span>
                </button>
                <div
                    v-if="wifiExpanded"
                    class="mt-2 p-3 border border-dashed border-gray-300 rounded-12 bg-gray-50 flex flex-col gap-2"
                    data-test="wifi-form"
                >
                    <label class="text-xs text-gray-600">
                        {{ t('clipboard.service.wifi.ssidLabel') }}
                        <InputText
                            v-model="wifiSsid"
                            class="w-full mt-1"
                            data-test="wifi-ssid"
                        />
                    </label>
                    <label class="text-xs text-gray-600">
                        {{ t('clipboard.service.wifi.passwordLabel') }}
                        <InputText
                            v-model="wifiPassword"
                            class="w-full mt-1"
                            data-test="wifi-password"
                        />
                    </label>
                    <button
                        type="button"
                        class="self-end border rounded px-3 py-1.5 text-sm bg-white hover:bg-gray-50"
                        data-test="wifi-add"
                        @click="addWifi"
                    >
                        {{ t('clipboard.service.wifi.addBtn') }}
                    </button>
                </div>
            </div>

            <!-- START-UPDATE (single-tile) -->
            <div class="mb-3">
                <div class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 flex items-center gap-1.5">
                    <Download class="w-3.5 h-3.5" />
                    {{ t('clipboard.service.startUpdate.heading') }}
                    <span class="font-normal normal-case text-gray-400">— {{ t('clipboard.service.startUpdate.subtitle') }}</span>
                </div>
                <div class="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    <button
                        type="button"
                        class="border rounded-12 p-2 bg-white hover:bg-gray-50 text-center text-sm"
                        data-test="tile-start-update"
                        @click="add({ kind: 'start-update' }, 'clipboard.service.startUpdate.label')"
                    >
                        {{ t('clipboard.service.startUpdate.short') }}
                    </button>
                </div>
            </div>

            <!-- RESET (single-tile, destructive accent) -->
            <div>
                <div class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 flex items-center gap-1.5">
                    <AlertTriangle class="w-3.5 h-3.5 text-red-500" />
                    {{ t('clipboard.service.reset.heading') }}
                    <span class="font-normal normal-case text-gray-400">— {{ t('clipboard.service.reset.subtitle') }}</span>
                </div>
                <div class="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    <button
                        type="button"
                        class="border-2 border-red-300 rounded-12 p-2 bg-white hover:bg-red-50 text-center text-sm"
                        data-test="tile-reset"
                        :title="t('clipboard.service.reset.subtitle')"
                        @click="add({ kind: 'reset' }, 'clipboard.service.reset.label')"
                    >
                        {{ t('clipboard.service.reset.short') }}
                    </button>
                </div>
            </div>
        </section>

        <section>
            <h3 class="text-sm font-semibold mb-3">{{ t('clipboard.service.groups.scanner') }}</h3>

            <!-- COUNTER -->
            <div class="mb-3">
                <div class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 flex items-center gap-1.5">
                    <Hash class="w-3.5 h-3.5" />
                    {{ t('clipboard.service.counter.heading') }}
                    <span class="font-normal normal-case text-gray-400">— {{ t('clipboard.service.counter.subtitle') }}</span>
                </div>
                <div class="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    <button
                        type="button"
                        class="border rounded-12 p-2 bg-white hover:bg-gray-50 text-center text-sm"
                        data-test="tile-counter-1"
                        @click="add({ kind: 'counter', variant: 1 }, 'clipboard.service.counter.on')"
                    >
                        {{ t('clipboard.service.counter.short.on') }}
                    </button>
                    <button
                        type="button"
                        class="border rounded-12 p-2 bg-white hover:bg-gray-50 text-center text-sm"
                        data-test="tile-counter-0"
                        @click="add({ kind: 'counter', variant: 0 }, 'clipboard.service.counter.off')"
                    >
                        {{ t('clipboard.service.counter.short.off') }}
                    </button>
                </div>
            </div>

            <!-- LANGUAGE -->
            <div class="mb-3">
                <div class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 flex items-center gap-1.5">
                    <Languages class="w-3.5 h-3.5" />
                    {{ t('clipboard.service.language.heading') }}
                    <span class="font-normal normal-case text-gray-400">— {{ t('clipboard.service.language.subtitle') }}</span>
                </div>
                <div class="grid grid-cols-3 sm:grid-cols-4 gap-2">
                    <button
                        v-for="lang in LANGS"
                        :key="lang"
                        type="button"
                        class="border rounded-12 p-2 bg-white hover:bg-gray-50 text-center text-sm"
                        :data-test="`tile-language-${lang}`"
                        @click="add({ kind: 'language', variant: lang }, `clipboard.service.language.${lang}`)"
                    >
                        {{ t(`clipboard.service.language.short.${lang}`) }}
                    </button>
                </div>
            </div>

            <!-- SCAN-INFO -->
            <div>
                <div class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 flex items-center gap-1.5">
                    <Eye class="w-3.5 h-3.5" />
                    {{ t('clipboard.service.scanInfo.heading') }}
                    <span class="font-normal normal-case text-gray-400">— {{ t('clipboard.service.scanInfo.subtitle') }}</span>
                </div>
                <div class="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    <button
                        v-for="mode in SCAN_MODES"
                        :key="mode"
                        type="button"
                        class="border rounded-12 p-2 bg-white hover:bg-gray-50 text-center text-sm"
                        :data-test="`tile-scaninfo-${mode}`"
                        @click="add({ kind: 'scan-info', variant: mode }, `clipboard.service.scanInfo.${mode.toLowerCase()}`)"
                    >
                        {{ t(`clipboard.service.scanInfo.short.${mode.toLowerCase()}`) }}
                    </button>
                </div>
            </div>
        </section>

        <template #footer>
            <BBtn severity="secondary" @click="close">
                {{ t('clipboard.service.modal.close') }}
            </BBtn>
        </template>
    </Dialog>
</template>
